<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Actuals - On-time Performance Report</span><br/>
      <span style="font-style: italic; font-size: 18px">this report highlights differences between planned & actual times.  click any type or status to filter results or any column heading to sort results.</span>
    </div>

    <div class="perf-otp-filter-rows">
    <div class="perf-otp-filter-row align-right d-flex align-items-center justify-content-center">
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;&nbsp;<date-picker style="font-size: 16px; font-weight: bold; width:130px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>&nbsp;&nbsp;
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;
      Early Limit&nbsp;<b-form-select v-model="early_threshold" :options="threshold_options" style="background-color: hsla(120,100%,75%,0.2);font-size: 16px; font-weight: bold; width:80px"/>
      &nbsp;
      Late Limit&nbsp;<b-form-select v-model="late_threshold" :options="threshold_options" class="late" style="background-color: rgba(255,0,0,0.2); font-size: 16px; font-weight: bold; width:80px"/>
      &nbsp;
      Filter Results&nbsp;<b-form-input style="width: 180px; font-size: 16px;" v-model="booking_filter" placeholder="charterID,vehicle,driver"/>
      <template v-if="summary.bases && summary.bases.length > 2">&nbsp;Base&nbsp;<b-form-select v-model="selected_company" :options="summary.bases" @change="filter_items()" style="width: 180px; font-size: 16px; font-weight: bold;"/></template>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
      <div class="d-flex align-items-center justify-content-center">
        <b-button size="md" variant="success" @click="download">Download</b-button>
      </div>
    </div>

    <div v-if="!loading" class="perf-otp-filter-chips actuals-filter-chips">
      <span
        class="actuals-chip actuals-chip-clickable actuals-chip-total"
        :class="{ 'actuals-chip-selected': selected_status == null }"
        @click="filter_by_status(null)"
      >{{ items.length }}/{{ summary.total_cnt }}</span>
      <span class="actuals-filter-sep">|</span>
      <span class="actuals-filter-label">Timing</span>
      <span
        class="actuals-chip actuals-chip-clickable ontime"
        :class="{ 'actuals-chip-selected': selected_status === 'ontime' }"
        @click="filter_by_status('ontime')"
      >On Time {{ ontime_items.length }}</span>
      <span
        class="actuals-chip actuals-chip-clickable early"
        :class="{ 'actuals-chip-selected': selected_status === 'early' }"
        @click="filter_by_status('early')"
      >Early {{ early_items.length }}</span>
      <span
        class="actuals-chip actuals-chip-clickable late"
        :class="{ 'actuals-chip-selected': selected_status === 'late' }"
        @click="filter_by_status('late')"
      >Late {{ late_items.length }}</span>
      <template v-if="summary.booking_types && summary.booking_types.length > 1">
        <span class="actuals-filter-sep">|</span>
        <span class="actuals-filter-label">Type</span>
        <span
          class="actuals-chip actuals-chip-clickable booking-type-filter"
          :class="{ 'actuals-chip-selected': selected_type == null }"
          @click="filter_by_type(null)"
        >All Types</span>
        <span
          v-for="type in summary.booking_types"
          :key="type"
          class="actuals-chip actuals-chip-clickable booking-type-filter"
          :class="{ 'actuals-chip-selected': selected_type == type }"
          @click="filter_by_type(type)"
        >{{ type_label(type) }}</span>
      </template>
    </div>
    </div>

    <table v-if="!loading" width="100%" border="1" cellpadding="2">
      <tr>
        <th colspan="3" style="font-size: 18px; font-weight: bold;">&nbsp;</th>
        <th style="background-color: lightgrey" colspan="3" v-for="time_field in summary.time_fields" @click="sort_dates('planned' + time_field + 'DateTime')">{{time_field}}</th>
      </tr>
      <tr>
        <th width="60px" @click="sort_numbers('charterID')">Booking ID</th>
        <th width="60px" @click="sort_strings('vehicle')">Vehicle</th>
        <th width="60px" @click="sort_strings('driver')">Driver</th>
        <template v-for="time_field in summary.time_fields">
          <th width="60px" @click="sort_dates('planned' + time_field + 'DateTime')">Planned</th>
          <th width="60px" @click="sort_dates('actual' + time_field + 'DateTime')">Actual</th>
          <th width="30px" @click="sort_numbers(time_field + 'Delta')">Diff</th>
        </template>
      </tr>
      <template v-for="item in items">
        <tr>
          <td align="center">
            <router-link v-if="item.tbn_booking_hash" target="_blank" style="font-weight: bold;" :to="{ name: 'actuals_tbn', query: { booking_id: item.tbn_booking_hash } }">{{ item.bookingID }}</router-link>
            <span v-else>{{ item.bookingID }}</span>
          </td>
          <td align="center">
            <router-link v-if="item.vehicle" target="_blank" style="font-weight: bold;" :to="{ name: 'vehicle_trip_history', query: { vehicleId: item.vehicle, trip_date: reportDateIso } }">{{ item.vehicle }}</router-link>
            <span v-else>&nbsp;</span>
          </td>
          <td align="center">
            <a v-if="driverHosLink(item)" target="_blank" style="font-weight: bold;" :href="driverHosLink(item)">{{ item.driver }}</a>
            <span v-else>{{ item.driver }}</span>
          </td>
          <template v-for="time in item.times">
            <td>{{format_local(time.planned, item.timezone)}}</td>
            <td>{{format_local(time.actual, item.timezone)}}</td>
            <td v-if="time.actual && isEarlyDelta(time.delta)" class="early">{{time.delta}}m</td>
            <td v-else-if="time.actual && isLateDelta(time.delta)" class="late">{{time.delta}}m</td>
            <td v-else-if="time.delta">{{time.delta}}m</td>
            <td v-else>&nbsp;</td>
          </template>
        </tr>
      </template>
    </table>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>

</template>
<script>
import {get_tbn_actuals_perf_report, tbn_api_url } from '../networking/tbn_apis'
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'

export default {
  name: 'actuals_perf_report_tbn',

  components: {
  },

  data() {
    return {
      booking_filter: '',
      items: [],
      raw_results: [],
      ontime_items: [],
      early_items: [],
      late_items: [],
      summary: [],
      early_threshold: 10,
      late_threshold: 10,
      selected_type: null,
      selected_status: null,
      selected_company: '',
      threshold_options: [
        { value: '1', text: '1m' },
        { value: '5', text: '5m' },
        { value: '10', text: '10m' },
        { value: '15', text: '15m' },
        { value: '20', text: '20m' },
        { value: '30', text: '30m' },
        { value: '45', text: '45m' },
        { value: '60', text: '1h' },
        { value: '120', text: '2h' },
        { value: '240', text: '4h' },
      ],
      loading: false,
      details_fromDate: justDate(moment().subtract(1, 'days')),

      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },
  created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const {value: {data = []}} = await get_tbn_actuals_perf_report (this.user.tenant, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.raw_results = data.results || []
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.applyThresholdFilters()
      } catch(e){
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    format_local(value, tz) {
      if (!value || typeof value !== 'string') return ''
      const m = moment(String(value))
      const effective_tz = tz || (this.summary && this.summary.timezone)
      if (effective_tz) m.tz(effective_tz)
      return m.format('MM/DD hh:mm A')
    },
    isEarlyDelta(delta) {
      if (delta == null || delta === '') return false
      const value = Number(delta)
      return !Number.isNaN(value) && value < 0 && Math.abs(value) >= parseInt(this.early_threshold, 10)
    },
    isLateDelta(delta) {
      if (delta == null || delta === '') return false
      const value = Number(delta)
      return !Number.isNaN(value) && value > 0 && Math.abs(value) >= parseInt(this.late_threshold, 10)
    },
    applyThresholdFilters() {
      if (!this.raw_results.length) {
        this.items_full = []
        this.items = []
        this.summary = this.summary || {}
        this.summary.total_cnt = 0
        return
      }
      this.items_full = this.raw_results.filter(item => this.itemHasActuals(item))
      this.calc_otp_status()
    },
    itemHasActuals(item) {
      return (item.times || []).some(time => time.actual)
    },
    calc_otp_status() {
      for (const item of this.items_full || []) {
        let otp_status = ''
        for (const time of item.times || []) {
          if (time.actual && this.isEarlyDelta(time.delta)) {
            otp_status += '+early'
          } else if (time.actual && this.isLateDelta(time.delta)) {
            otp_status += '+late'
          }
        }
        item.otp_status = otp_status || 'ontime'
      }
      this.filter_items()
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    countSourceItems() {
      let source = this.items_full || []
      if (this.selected_company === '__other__')
        source = source.filter(item => !item.start_base)
      else if (this.selected_company)
        source = source.filter(item => item.start_base == this.selected_company)
      if (this.selected_type === '__other__')
        source = source.filter(item => !item.booking_type)
      else if (this.selected_type)
        source = source.filter(item => item.booking_type == this.selected_type)
      if (this.booking_filter)
        source = source.filter(item => (this.contains(item.bookingID, this.booking_filter) || this.contains(item.driver, this.booking_filter) || this.contains(item.vehicle, this.booking_filter)))
      return source
    },
    filter_items() {
      const counts = this.countSourceItems()
      if (this.summary) {
        this.summary = { ...this.summary, total_cnt: counts.length }
      }
      this.items = counts
      if (this.selected_status)
        this.items = this.items.filter(item => item.otp_status.indexOf(this.selected_status) != -1)
      this.ontime_items = counts.filter(item => item.otp_status && item.otp_status.indexOf('ontime') != -1)
      this.early_items = counts.filter(item => item.otp_status && item.otp_status.indexOf('early') != -1)
      this.late_items = counts.filter(item => item.otp_status && item.otp_status.indexOf('late') != -1)
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    filter_by_type(type) {
      this.selected_type = (type == null || this.selected_type == type) ? null : type
      this.filter_items()
    },
    filter_by_status(status) {
      if (status == null || this.selected_status == status) {
        this.selected_status = null
      } else {
        this.selected_status = status
      }
      this.filter_items()
    },
    type_label(type) {
      const labels = { 'booking': 'Charter', 'charter': 'Charter', 'scheduledservice': 'Scheduled Service', 'schoolcontract': 'School Contract', '__other__': 'Other' }
      return labels[type] || type
    },
    driverHosLink(item) {
      if (item.driver_hos_link) return item.driver_hos_link
      if (!this.summary || !this.summary.org_id || !item.samsara_driver_id) return null
      return `https://cloud.samsara.com/o/${this.summary.org_id}/fleet/reports/hos/driver/driver/${item.samsara_driver_id}?end_ms=${this.reportEndMs}`
    },
    sort_dates(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return new Date(a[col]) - new Date(b[col])
          }
        })
      }
      this.sort_col = col
    },
    sort_numbers(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return a[col] - b[col]
          }
        })
      }
      this.sort_col = col
    },
    sort_strings(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            let fa = a[col].charAt(0).toLowerCase(),
                fb = b[col].charAt(0).toLowerCase();

            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          }
        })
      }
      this.sort_col = col
    },
    async download() {
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${tbn_api_url}tbn/reports/actuals/performance/download`,
          {
            tenant: this.user.tenant,
            date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            base: this.selected_company || null,
            booking_type: this.selected_type || null,
            early_threshold: this.early_threshold,
            late_threshold: this.late_threshold,
            status: this.selected_status || null,
            booking_filter: this.booking_filter || null,
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `actuals-perf-${this.user.tenant}-${this.details_fromDate}.csv`)
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },

  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    reportDateIso() {
      return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
    },
    reportEndMs() {
      const tz = this.summary && this.summary.timezone
      const dayStart = tz
        ? moment.tz(this.details_fromDate, 'MM-DD-YYYY', tz).startOf('day')
        : moment(this.details_fromDate, 'MM-DD-YYYY').startOf('day')
      return Math.floor(dayStart.valueOf()) + 43199999 + 25200000
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
    details_fromDate(target_from_date) {
      this.selected_type = null
      this.selected_company = ''
      this.details_fromDate = target_from_date
      this.load_data();
    },
    early_threshold() {
      this.applyThresholdFilters()
    },
    late_threshold() {
      this.applyThresholdFilters()
    },
    booking_filter() {
      this.filter_items()
    },
  }
}
</script>

<style>
.arrows {font-size: 22px; font-weight: bold; color: var(--fi-rail-accent) !important }

.perf-otp-filter-rows {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}
.perf-otp-filter-row {
  position: relative;
  font-weight: bold;
  font-size: 18px;
  flex-wrap: nowrap;
  white-space: nowrap;
}
.perf-otp-filter-chips {
  margin: 0 30px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px 9px;
}
.actuals-filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
}
.actuals-filter-sep {
  color: #adb5bd;
  margin: 0 2px;
}
.actuals-chip {
  display: inline-block;
  padding: 5px 12px;
  border: 1px solid #ccc;
  border-radius: 15px;
  font-size: 14px;
  font-weight: 600;
  background-color: #f8f9fa;
  white-space: nowrap;
}
.actuals-chip-clickable {
  cursor: pointer;
}
.actuals-chip-clickable:hover {
  opacity: 0.88;
}
.actuals-chip-selected:not(.actuals-chip-total) {
  outline: 2px solid #495057;
  outline-offset: 1px;
}
.actuals-chip-total.actuals-chip-selected {
  border: 1px solid #ccc !important;
  box-shadow: none !important;
  outline: none;
}

.early {
  background-color: hsla(120, 100%, 75%, 0.55);
  border-color: #7dcc7d;
}
.late {
  background-color: rgba(255, 0, 0, 0.2);
  border-color: #e88;
}
.ontime {
  background-color: hsla(200, 75%, 50%, 0.35);
  border-color: #6aaed6;
}
.booking-type-filter {
  background-color: hsla(250, 60%, 70%, 0.45);
  border-color: #b9b;
}

table .early {
  background-color: hsla(120, 100%, 75%, 0.2);
  border-color: transparent;
}
table .late {
  background-color: rgba(255, 0, 0, 0.2);
  border-color: transparent;
}

</style>
